import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CreditView, OpsExpress, OpsExpressRun } from '@popcart/core/v1';
import { api, errorMessage } from '../../api';
import { ErrorState, Loading, useNow, useTitle } from '../../ui/bits';
import { Fig, PageHead, RowMenu, Stat } from '../../ui/viz';
import { useToast } from '../../ui/toast';
import { Icon } from '../../ui/Icon';
import { count, dayStamp, peso, percent } from '../../lib/format.js';
import { opsKeys, useOpsRiders } from '../queries';
import { GaugeRing } from '../parts';
import { expressKeys, useOpsExpress } from './queries';
import {
  MAX_ACTIVE,
  STATE_PILL,
  capacityView,
  clampMaxActive,
  countdownText,
  creditSummary,
  deadlineAt,
  freeRiders,
  liveState,
  pausePrompt,
  projectedLabel,
  sortRuns,
  unassignedLabel,
} from './lib.js';
import { AssignDialog, CreditDialog, PauseDialog } from './dialogs';
import './express.css';

/**
 * Ops · Express monitor (docs/PHASE6-CONTRACT.md): capacity, today's guarantee numbers and the
 * live Express runs (late → at risk → minutes left) with local countdowns. Polls every 10 s.
 */
export function ExpressPage() {
  useTitle('Express');
  const qc = useQueryClient();
  const toast = useToast();
  const monitor = useOpsExpress();
  const riders = useOpsRiders();
  const now = useNow(1000);

  const data = monitor.data;
  const fetchedAt = monitor.dataUpdatedAt;
  const runs = useMemo(() => sortRuns(data?.runs ?? []), [data?.runs]);
  const free = useMemo(() => freeRiders(riders.data), [riders.data]);

  const [pauseOpen, setPauseOpen] = useState(false);
  const [assignRun, setAssignRun] = useState<OpsExpressRun | null>(null);
  const [creditRun, setCreditRun] = useState<OpsExpressRun | null>(null);
  const [created, setCreated] = useState<CreditView | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: expressKeys.monitor });
    void qc.invalidateQueries({ queryKey: opsKeys.settings });
  };

  const setExpress = useMutation({
    mutationFn: (patch: { enabled?: boolean; maxActive?: number }) => api.putOpsSetting('express', patch),
    onSuccess: (_v, patch) => {
      // Reflect it at once; the next poll confirms.
      qc.setQueryData<OpsExpress>(expressKeys.monitor, (old) => {
        if (!old) return old;
        const max = patch.maxActive ?? old.capacity.max;
        const cap = capacityView({ active: old.capacity.active, max }, patch.enabled ?? old.enabled);
        return { ...old, enabled: patch.enabled ?? old.enabled, capacity: { ...old.capacity, max, full: cap.full, pct: cap.pct } };
      });
      if (patch.enabled !== undefined) toast(patch.enabled ? 'Express is back on. Customers can choose it again.' : 'Express is paused. Runs on the road keep their promise.');
      else if (patch.maxActive !== undefined) toast(`Express now takes up to ${patch.maxActive} runs at once.`);
      setPauseOpen(false);
      refresh();
    },
    onError: (err) => toast(errorMessage(err, "Couldn't change Express. Try again."), 'error'),
  });

  const assign = useMutation({
    mutationFn: (v: { deliveryId: string; riderUserId: string }) => api.assignDelivery(v.deliveryId, v.riderUserId),
    onSuccess: (_d, v) => {
      const who = free.find((r) => r.userId === v.riderUserId)?.name ?? 'The rider';
      toast(`${who} is on ${assignRun?.code ?? 'the run'}.`);
      setAssignRun(null);
      void qc.invalidateQueries({ queryKey: ['ops'] });
    },
    onError: (err) => toast(errorMessage(err, "Couldn't assign. Try again."), 'error'),
  });

  const issue = useMutation({
    mutationFn: (v: { orderId: string; amountCentavos: number; note: string }) => api.issueCredit(v),
    onSuccess: (credit) => {
      setCreated(credit);
      toast(creditSummary(credit, creditRun?.code ?? credit.sourceOrderCode));
      void qc.invalidateQueries({ queryKey: expressKeys.monitor });
    },
  });

  const closeCredit = () => {
    setCreditRun(null);
    setCreated(null);
    issue.reset();
  };

  const late = runs.filter((r) => r.state === 'late').length;
  const risk = runs.filter((r) => r.state === 'at_risk').length;
  const ago = fetchedAt ? Math.max(0, Math.round((now - fetchedAt) / 1000)) : null;

  return (
    <>
      <PageHead
        title="Express"
        live={!!data?.enabled}
        meta={
          <span aria-live="off" title="Refreshes every 10 s">
            {dayStamp(new Date(now))}
            {ago !== null ? ` · ${ago}s` : ''}
          </span>
        }
        actions={
          data ? (
            <button type="button" role="switch" className="switch" aria-checked={data.enabled} disabled={setExpress.isPending} onClick={() => setPauseOpen(true)}>
              <span className="track" aria-hidden="true" />
              <span className="t-label">{data.enabled ? 'Express is on' : 'Express is paused'}</span>
            </button>
          ) : null
        }
      />

      {monitor.isError && !data ? (
        <section className="card">
          <ErrorState error={monitor.error} onRetry={() => void monitor.refetch()} />
        </section>
      ) : !data ? (
        <Loading label="Loading Express" rows={3} />
      ) : (
        <>
          <Today today={data.today} />
          <div className="dv-grid">
            <Capacity data={data} busy={setExpress.isPending} onMax={(maxActive) => setExpress.mutate({ maxActive })} />
            <section className="card dv-card dv-span-8" aria-labelledby="xp-runs-h">
              <div className="dv-card-h">
                <h2 id="xp-runs-h">Live runs</h2>
                {late ? <span className="dv-chip bad">{count(late)} late</span> : null}
                {risk ? <span className="dv-chip xp-risk">{count(risk)} at risk</span> : null}
                <span className="grow" />
                <span aria-label={`${runs.length} on the road`}>
                  <Fig text={count(runs.length)} size="sm" animate={false} />
                </span>
              </div>
              {monitor.isError ? (
                <p className="pill st-warning err-pill" role="status">
                  Couldn't refresh. Showing the last update; trying again in 10 s.
                </p>
              ) : null}
              {runs.length ? (
                <ul className="xp-runs" aria-label="Express runs, most urgent first">
                  {runs.map((r) => (
                    <RunCard key={r.deliveryId} run={r} fetchedAt={fetchedAt} now={now} onAssign={() => setAssignRun(r)} onCredit={() => setCreditRun(r)} />
                  ))}
                </ul>
              ) : (
                <div className="dv-quiet xp-quiet" title={data.enabled ? 'New Express orders show up here the moment they’re placed.' : 'Express is paused. Resume it to take Express orders again.'}>
                  <Icon name={data.enabled ? 'check' : 'bolt'} />
                  {data.enabled ? 'No Express runs right now' : 'Express is paused'}
                </div>
              )}
            </section>
          </div>
        </>
      )}

      {data ? (
        <PauseDialog
          open={pauseOpen}
          prompt={pausePrompt(data.enabled, data.capacity.active)}
          busy={setExpress.isPending}
          onConfirm={() => setExpress.mutate(pausePrompt(data.enabled, data.capacity.active).patch)}
          onClose={() => setPauseOpen(false)}
        />
      ) : null}
      <AssignDialog
        run={assignRun}
        riders={free}
        loading={!riders.data}
        busy={assign.isPending}
        onAssign={(riderUserId) => assignRun && assign.mutate({ deliveryId: assignRun.deliveryId, riderUserId })}
        onClose={() => setAssignRun(null)}
      />
      <CreditDialog
        order={creditRun}
        busy={issue.isPending}
        created={created}
        error={issue.isError ? errorMessage(issue.error, "Couldn't issue the credit. Try again.") : null}
        onIssue={(v) => creditRun && issue.mutate({ orderId: creditRun.orderId, ...v })}
        onClose={closeCredit}
      />
    </>
  );
}

/** Capacity: a ring of active / max runs, free riders, and the max-at-once stepper. */
function Capacity({ data, busy, onMax }: { data: OpsExpress; busy: boolean; onMax(n: number): void }) {
  const cap = capacityView(data.capacity, data.enabled);
  const [draft, setDraft] = useState<number | null>(null);
  const max = draft ?? data.capacity.max;
  const changed = draft !== null && draft !== data.capacity.max;
  const tone = cap.tone === 'full' ? 'bad' : cap.pct >= 80 ? 'warn' : 'good';
  return (
    <section className="card dv-card dv-span-4 xp-cap" aria-labelledby="xp-cap-h">
      <div className="dv-card-h">
        <h2 id="xp-cap-h">Capacity</h2>
        <span className="grow" />
        {cap.tone === 'full' ? <span className="dv-chip bad">Full</span> : cap.tone === 'off' ? <span className="dv-chip">Off</span> : <span className="dv-chip good">Taking orders</span>}
      </div>
      <div
        className={cap.tone === 'off' ? 'xp-gauge off' : 'xp-gauge'}
        role="meter"
        aria-label="Express capacity"
        aria-valuemin={0}
        aria-valuemax={data.capacity.max}
        aria-valuenow={Math.min(data.capacity.active, data.capacity.max)}
        aria-valuetext={cap.label}
      >
        <GaugeRing pct={cap.pct} tone={tone} size={176}>
          <span className="xp-gauge-mid">
            <Fig text={`${count(data.capacity.active)} / ${count(data.capacity.max)}`} size="lg" />
            <span className="dv-sub">runs</span>
          </span>
        </GaugeRing>
        <span className="dv-sub xp-note">{cap.note}</span>
      </div>
      <div className="xp-cap-foot">
        <div>
          <span className="dv-label">Free riders</span>
          <Fig text={count(data.onlineFreeRiders)} size="md" />
        </div>
        <div>
          <span className="dv-label" id="xp-max-l">
            Max at once
          </span>
          <div className="row">
            <div className="stepper" role="group" aria-labelledby="xp-max-l">
              <button type="button" className="btn icon-btn xp-step" aria-label="One fewer" disabled={busy || max <= MAX_ACTIVE.min} onClick={() => setDraft(clampMaxActive(max - 1))}>
                <Icon name="minus" />
              </button>
              <output aria-live="polite" className="fig sm">
                {max}
              </output>
              <button type="button" className="btn icon-btn xp-step" aria-label="One more" disabled={busy || max >= MAX_ACTIVE.max} onClick={() => setDraft(clampMaxActive(max + 1))}>
                <Icon name="plus" />
              </button>
            </div>
            {changed ? (
              <button
                type="button"
                className="btn btn-secondary xp-step"
                disabled={busy}
                aria-busy={busy || undefined}
                onClick={() => {
                  onMax(max);
                  setDraft(null);
                }}
              >
                Save
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

function onTimeTone(p: number | null): 'good' | 'warn' | 'bad' {
  if (p === null) return 'good';
  return p >= 90 ? 'good' : p >= 75 ? 'warn' : 'bad';
}

/** Today's guarantee numbers as stat tiles. */
function Today({ today }: { today: OpsExpress['today'] }) {
  return (
    <section aria-label="Today" className="dv-stats lx-stats xp-stats">
      <Stat
        label="Delivered"
        value={count(today.delivered)}
        visual={
          <span className="dv-disc" aria-hidden="true">
            <Icon name="bolt" />
          </span>
        }
      />
      <Stat
        label="On time"
        value={percent(today.onTimePct)}
        sub={today.delivered ? `${count(today.onTime)} of ${count(today.delivered)}` : undefined}
        visual={<GaugeRing pct={today.onTimePct} tone={onTimeTone(today.onTimePct)} size={64} />}
      />
      <Stat label="Late" value={count(today.late)} tone={today.late ? 'alert' : undefined} sub={today.late ? 'Each got a credit' : undefined} />
      <Stat label="Credits" value={peso(today.creditsIssuedCentavos)} sub={<span title="Late and goodwill credits issued today">Late + goodwill</span>} />
      <Stat label="Surcharge" value={peso(today.surchargeCentavos)} />
    </section>
  );
}

/** One live run: the ticking promise clock leads; Assign is the one visible action. */
function RunCard({ run, fetchedAt, now, onAssign, onCredit }: { run: OpsExpressRun; fetchedAt: number; now: number; onAssign(): void; onCredit(): void }) {
  const msLeft = deadlineAt(run, fetchedAt) - now;
  const clock = countdownText(msLeft);
  const state = liveState(run.state, msLeft);
  const pill = STATE_PILL[state];
  const projected = projectedLabel(run);
  const waiting = run.rider ? null : unassignedLabel(run.unassignedSeconds, fetchedAt, now);
  return (
    <li className={`xp-run xp-${state}`}>
      <span className="xp-clock" aria-label={`Promise: ${clock.label}`}>
        <span className="fig md" aria-hidden="true">
          {clock.text}
        </span>
        <span className="xp-clock-l" aria-hidden="true">
          <span className={`xp-dot ${state}`} />
          {pill.label}
        </span>
      </span>
      <span className="xp-what">
        <span className="xp-what-top">
          <span className="lb-code">{run.code}</span>
          {projected ? (
            <span className="dv-chip xp-risk" title={projected}>
              {projected}
            </span>
          ) : null}
          {run.escalated ? (
            <span className="dv-chip bad xp-esc">
              <Icon name="bell" size="sm" />
              Escalated
            </span>
          ) : null}
        </span>
        <span className="xp-what-sub">
          <Icon name="bag" size="sm" />
          <span className="xp-shops">{run.shopNames.join(' → ')}</span>
          <span className="xp-for">· {run.customerFirstName}</span>
        </span>
      </span>
      <span className="xp-who">
        {run.rider ? (
          <span className="xp-rider">
            <span className="rd-st busy" aria-hidden="true" />
            {run.rider.name}
          </span>
        ) : (
          <span className="xp-unassigned">{waiting ?? 'Unassigned'}</span>
        )}
      </span>
      <span className="xp-act">
        {run.rider ? null : (
          <button type="button" className="btn" onClick={onAssign}>
            <Icon name="pin" />
            Assign rider
          </button>
        )}
        <RowMenu label={`More for ${run.code}`} items={[{ label: 'Goodwill credit', icon: 'refund', onSelect: onCredit }]} />
      </span>
    </li>
  );
}
